import { getCurrentInstance, ref } from 'vue';
import { irstore } from '@/fetch/interface';
import axios from '@/axios';
import to from 'await-to-js';
import { isErrorValue } from '@/utils/api';
import { useStore } from '@/utils/vue-api';
import { resolveStoreId, resolvePageId } from '@rebuild/utils/context';
import { getLanguageType } from '@/assets/js/i18n';
import LoadingMask from '@rebuild/components/loading/loading';
import { useCommonData } from '@rebuild/hooks/useCommonData';

export function useFuncStep() {
	async function runSteps(steps) {
		let hasError = false;
		const results = [];

		for (const step of steps) {
			const result = await step();
			results.push(result);
			if (result.error) {
				hasError = true;
			}
		}

		return { hasError, results };
	}

	function wrapStep(fn) {
		return async () => {
			try {
				const data = await fn();
				// 如果函数 return 的结果是 falsy，也算失败
				if (data === undefined || data === null) {
					return { error: 'falsy result', data: null };
				}
				return { error: null, data };
			} catch (err) {
				return { error: err, data: null };
			}
		};
	}

	return {
		runSteps,
		wrapStep
	};
}

export function useCoreData() {
	const vm = getCurrentInstance();
	const store = useStore();

	const responseData = res => res?.data;

	const { viewInIframe, viewInPreview, multiTemplates } = useCommonData();

	// ===================== 加载状态 =====================
	const isLoading = ref(true);
	const showLoading = () => {
		isLoading.value = true;
		LoadingMask.show();
	};
	const hideLoading = () => {
		LoadingMask.hide();
		isLoading.value = false;
	};

	// ===================== 基础数据 =====================
	const loadIRBasicInfo = async () => {
		const storeId = resolveStoreId();
		if (isErrorValue('storeId', storeId)) return;

		const [err, res] = await to(axios.get(irstore.getBaseInfo(storeId)));
		if (err) return;

		const data = responseData(res) || {};
		store.commit('updateIrBaseInfo', data);
		return data;
	};
	const loadIRPageData = async () => {
		const storeId = resolveStoreId();
		const pageId = resolvePageId();
		if (isErrorValue('storeId', storeId) || isErrorValue('pageId', pageId)) return;

		const usePreviewApi = viewInIframe.value || viewInPreview.value;
		const queryApi = usePreviewApi
			? irstore.getPageSaveContent(storeId, pageId)
			: irstore.getMenuData(storeId, pageId);
		const [err, res] = await to(axios.get(queryApi));
		if (err) return;

		const data = responseData(res) || {};
		setIRStoreData(data);
		return data;
	};
	const setIRStoreData = data => {
		store.commit('updateIrCoreConfig', data?.quickLinkVOList || []); // 「快速入口」的数据
		store.commit('updateIrTemplateData', data);
	};

	const queryPageList = async tmplId => {
		if (!tmplId) return null;

		const storeId = resolveStoreId();
		if (isErrorValue('storeId', storeId)) return null;

		const [err, res] = await to(axios.post(irstore.rebuild.queryPageListWithTemplateId(storeId, tmplId)));
		if (err) return null;

		return responseData(res);
	};

	// ===================== 模板配置表 =====================
	// 通过模板ID查配置表
	const loadUIConfigWithTmplId = async () => {
		const currTemplate = store.getters.getIrPageType;

		let tmplId = null,
			pageId = null,
			pageList = null;

		if (multiTemplates.includes(currTemplate)) {
			// 新模板，直接取链接上模板ID
			tmplId = vm.proxy.$route.params?.tmplId || vm.proxy.$route.query?.tmplId; // 兼容链接访问场景
			pageList = await queryPageList(tmplId);
			pageId = findPageIdWithCurrLanguage(pageList);
		} else {
			// 历史模板，先取页面ID，再查模板ID
			const { storePageInfoList = [] } = store.getters.getIrBaseInfo;
			pageList = storePageInfoList.filter(v => v.pageType === currTemplate);
			pageId = findPageIdWithCurrLanguage(pageList);
			tmplId = await getTemplateIdWithPageId(pageId);
		}

		store.commit('setTemplateId', tmplId);
		store.commit('setPageId', pageId);
		store.commit('setPageList', pageList);

		const config = await getTemplateConfig(tmplId);
		store.commit('setUiConfig', config || {});
		return config;
	};
	const findPageIdWithCurrLanguage = (pageList = []) => {
		const currLanguageType = getLanguageType();
		const findPageInfo = pageList?.find(v => v.languageType === currLanguageType);
		return findPageInfo && findPageInfo.pageId;
	};
	const getTemplateIdWithPageId = async pageId => {
		if (!pageId) return null;

		const storeId = resolveStoreId();
		if (isErrorValue('storeId', storeId)) return null;

		const [err, res] = await to(axios.get(irstore.rebuild.queryModuleIdWithPageId(storeId, pageId)));
		if (err) return null;

		return responseData(res);
	};
	const getTemplateConfig = async tmplId => {
		const storeId = resolveStoreId();
		if (isErrorValue('storeId', storeId) || !tmplId) return;

		const [err, res] = await to(
			axios.get(irstore.rebuild.queryTemplateConfig(storeId, tmplId), {
				languageType: getLanguageType()
			})
		);
		if (err) return;

		const data = responseData(res) || {};
		const { styleVOList = [], moduleConfigVOList = [] } = data;

		const modulesConfig = moduleConfigVOList.map(v => ({
			...v,
			id: v.moduleId
		}));

		const config = {
			layout: styleVOList,
			modules: modulesConfig
		};
		return config;
		// return transferConfig(config);
	};

	return {
		isLoading,
		showLoading,
		hideLoading,

		loadIRBasicInfo,
		loadIRPageData,
		setIRStoreData,
		loadUIConfigWithTmplId
	};
}

// TODO 定制内容验证
const transferConfig = config => {
	const version = 'v1';
	// const version = 'v1_HK02498';
	const { layout = [], modules = [] } = config;
	const _style = layout.map(v => ({
		...v,
		version: version
	}));
	const _modules = modules.map(v => ({
		...v,
		version: version
	}));
	return {
		layout: _style,
		modules: _modules
	};
};
